import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import PatientMiniProfile from '../../PatientMiniProfile/PatientMiniProfile';
import DatePicker from '../../Base/DatePicker/DatePicker';
import TimePicker from '../../Base/TimePicker/TimePicker';
import { IResponse } from '../../../Services/ApiClient';
import FormControl from '../../Form/FormControl';
import { useAppContext } from '../../../Services/AppContext';
import SubmitButton from '../../Button/SubmitButton';
import { useHealthPlanContext } from '../../../Services/HealthPlanContext';
import * as yup from 'yup';
import Text from '../../Base/Text/Text';
import PatientTimeZoneInfo, {
	PatientTimeZoneInfoValues,
} from '../../Patient/PatientTimeZoneInfo';
import useTranslation from '../../../Hooks/useTranslation';
import useHealthPlanAppointment from '../../../Modules/HealthPlan/Services/healthPlanAppointment.service';
import FormWrapper from '../../Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { HealthPlanCode } from '../../../Enums/HealthPlanCode.enum';
// @ts-ignore
import styles from '../../Calendar/CalendarPatientEvent.module.scss';
import { useIsLatam } from '../../../Modules/Region/Services/region.service';
import { getMomentDate } from '../../../Utils/dates.util';
import { updateHealthPlanUserItem } from '../../../Modules/HealthPlan/Services/healthPlan.service';
import { yupResolver } from '@hookform/resolvers/yup';
import TextAreaField from '../../Form/components/TextAreaField';
import CheckboxField from '../../Form/components/CheckboxField';
import SelectField from '../../Form/components/SelectField';
import InputWrapper from '../../Form/components/InputWrapper/InputWrapper';
import TimePickerField from '../../Form/components/TimePickerField/TimePickerField';

interface IAppointmentDetail {
	selectedDate: any;
	patientId: string;
	onSuccess?: () => void;
	workplaceId: string;
	order?: any;
	params?: any;
	onEventIsCreated?: (response: IResponse) => void;
}

const AppointmentDetail: React.FC<IAppointmentDetail> = props => {
	const {
		selectedDate,
		patientId,
		workplaceId,
		params,
		onSuccess,
		onEventIsCreated,
		order,
	} = props;
	const { translation, globalTemplate, userData } = useAppContext();
	const [meetingTypePick, setMeetingTypePick] = useState(
		params.allowPickMeetingType,
	);
	const { healthPlanUser, healthPlan, activeHealthPlanUserItem } =
		useHealthPlanContext();
	const [timeFrom, setTimeFrom] = useState<Moment | undefined>();
	const [timeTo, setTimeTo] = useState<Moment | undefined>();
	const [usersTimeDiff, setUsersTimeDiff] = useState<string | undefined>();
	const [patientTimezone, setPatientTimezone] = useState<string | undefined>();
	const { getDateRangeByMonth } = useHealthPlanAppointment();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const isLatam = useIsLatam(globalTemplate);

	const [defaultValues, setDefaultValues] = useState({
		date: selectedDate,
		timeFrom: selectedDate,
		timeTo: undefined,
		meetingType:
			activeHealthPlanUserItem?.extra?.data?.meetingType === 'onSite'
				? { value: 'onSite', label: t('base.meetingType.onSite') }
				: {
						value: 'telemedicine',
						label: t('base.meetingType.telemedicine'),
				  },
		paidMeeting: activeHealthPlanUserItem?.extra?.data?.paidMeeting ?? false,
	});

	const validationSchema = yup.object({
		meetingType: yup
			.mixed()
			.required('validation.calendarPatientEvent.meetingType'),
		date: yup.date().required('validation.calendarPatientEvent.date'),
		timeFrom: yup
			.date()
			.nullable()
			.default(undefined)
			.required('validation.calendarPatientEvent.timeFrom'),
		timeTo: yup
			.date()
			.nullable()
			.default(undefined)
			.required('validation.calendarPatientEvent.timeTo'),
		note: yup.string().notRequired(),
	});

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		resolver: yupResolver(validationSchema),
	});

	const formValues = formMethods.watch();

	const handleSetPatientTimezone = () => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();
		setTimeFrom(formValues.timeFrom);
		setTimeTo(formValues.timeTo);
	};

	useEffect(() => {
		if (patientId) {
			handleSetPatientTimezone();
		}
	}, []);

	const handleSave = async (values: any) => {
		setIsLoading(true);
		const date = getMomentDate(values.date);
		const timeFromMoment = getMomentDate(values.timeFrom);
		const timeToMoment = getMomentDate(values.timeTo);

		const timeFrom = date.clone().set({
			hour: timeFromMoment.hours(),
			minute: timeFromMoment.minutes(),
			second: timeFromMoment.seconds(),
		});

		const timeTo = date.clone().set({
			hour: timeToMoment.hours(),
			minute: timeToMoment.minutes(),
			second: timeToMoment.seconds(),
		});

		const dataToPatch: { [key: string]: any } = {
			note: values.note,
			date: date.toDate(),
			start: timeFrom,
			end: timeTo,
			paidMeeting: values.paidMeeting,
		};

		if (meetingTypePick) {
			dataToPatch['meetingType'] = values.meetingType?.value;
		}

		if (healthPlanUser?.healthPlanId && activeHealthPlanUserItem?.id) {
			const result = await updateHealthPlanUserItem({
				healthPlanId: healthPlanUser.healthPlanId,
				healthPlanUserId: healthPlanUser.id,
				healthPlanItemId: activeHealthPlanUserItem.id,
				data: {
					extra: {
						data: dataToPatch,
					},
				},
			});
			if (result) {
				onEventIsCreated?.(result);
				onSuccess?.();
			}
		}
		setIsLoading(false);
	};

	const handleGetPatientsTimes = () => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();
		setTimeFrom(formValues.timeFrom);
		setTimeTo(formValues.timeTo);
	};

	const handlePatientTimeDiffChange = (change: PatientTimeZoneInfoValues) => {
		setUsersTimeDiff(change.timeDiff);
		setPatientTimezone(change.patientTimezone);
	};

	useEffect(() => {
		if (!order) {
			return;
		}

		const tempDefaultValues: any = {
			date: order?.event?.start ? getMomentDate(order.event.start) : undefined,
			timeFrom: order?.event?.start
				? getMomentDate(order.event.start)
				: undefined,
			timeTo: order?.event?.end ? getMomentDate(order.event.end) : undefined,
			note: order?.event?.extendedProps?.note,
		};

		if (order) {
			const meta: { key: string; value: string }[] =
				order?.event?.extendedProps?.meta;
			if (meta) {
				const meetingTypeMeta = meta.find(m => m.key === 'meetingType');
				if (meetingTypeMeta) {
					if (meetingTypeMeta.value === 'telemedicine') {
						tempDefaultValues['meetingType'] = {
							value: 'telemedicine',
							label: t('base.meetingType.telemedicine'),
						};
					} else if (meetingTypeMeta.value === 'onSite') {
						tempDefaultValues['meetingType'] = {
							value: 'onSite',
							label: t('base.meetingType.onSite'),
						};
					}
				}
			}
		}

		setTimeFrom(tempDefaultValues.timeFrom);
		setTimeTo(tempDefaultValues.timeTo);
		setDefaultValues(tempDefaultValues);

		formMethods.reset(tempDefaultValues);
	}, [order]);

	useEffect(() => {
		handleGetPatientsTimes();
	}, [formValues.timeTo, formValues.timeFrom]);

	return (
		<>
			{patientId && (
				<div className='mb-4'>
					<PatientMiniProfile
						userId={patientId}
						workplaceId={workplaceId}
						loggedUserId={userData.id}
						showVerifyInformation={false}
						links={null}
						showAge={true}
						showBirthDate={false}
						showAddress={false}
						showPhone={false}
						showEmail={false}
						showInsuranceCompany={false}
						showGender={false}
					/>
				</div>
			)}
			{healthPlan.code === HealthPlanCode.DIABETES && (
				<div className={styles.plannedIn}>
					{t('component.appointmentDetail.meetingHasToBePlannedIn', {
						month: getDateRangeByMonth(
							healthPlanUser?.activeFrom
								? moment(healthPlanUser?.activeFrom)
								: moment(),
							activeHealthPlanUserItem?.extra?.month,
						),
					})}
				</div>
			)}
			{activeHealthPlanUserItem?.extra?.data?.meetingType &&
				!meetingTypePick && (
					<div
						style={{
							fontWeight: '500',
							fontSize: '14px',
							marginBottom: '20px',
							color: '#1d65d8',
						}}>
						<Text tx='component.eventCalendar.typeOfEvent' inline={true} />
						<Text tx=': ' inline={true} />
						{activeHealthPlanUserItem?.extra?.data?.meetingType ===
							'onSite' && <Text tx='base.meetingType.onSite' inline={true} />}
						{activeHealthPlanUserItem?.extra?.data?.meetingType ===
							'telemedicine' && (
							<Text tx='base.meetingType.telemedicine' inline={true} />
						)}
					</div>
				)}
			<FormWrapper formMethods={formMethods}>
				{meetingTypePick && (
					<InputWrapper>
						<SelectField
							name='meetingType'
							label='component.eventCalendar.typeOfEvent'
							options={[
								{
									value: 'telemedicine',
									label: 'base.meetingType.telemedicine',
								},
								{
									value: 'onSite',
									label: 'base.meetingType.onSite',
								},
							]}
							isRequired
						/>
					</InputWrapper>
				)}
				{!!usersTimeDiff && (
					<div className={styles.warning}>
						<Text tx='base.patientsIsInTimeZone.part1' inline firstUpper /> (
						{patientTimezone}){', '}
						<span className={styles.warningBold}>{usersTimeDiff}</span>{' '}
						<Text
							tx='base.shared.hourMiddle'
							className={styles.warningBold}
							inline
						/>{' '}
						<Text tx='base.patientsIsInTimeZone.part2' inline />.
					</div>
				)}
				<div className='row'>
					<div className='col-4'>
						<FormControl label='base.shared.date' name='date' isRequired>
							<DatePicker minDate={moment()} />
						</FormControl>
					</div>
					<div className='col-4'>
						<TimePickerField
							label='base.shared.from'
							name='timeFrom'
							isRequired
							isWrapped
						/>
						<PatientTimeZoneInfo
							patientId={patientId}
							time={timeFrom}
							onChange={handlePatientTimeDiffChange}
						/>
					</div>
					<div className='col-4'>
						<TimePickerField
							label='base.shared.to'
							name='timeTo'
							isRequired
							isWrapped
						/>
						<PatientTimeZoneInfo
							patientId={patientId}
							time={timeTo}
							onChange={handlePatientTimeDiffChange}
						/>
					</div>
				</div>
				<TextAreaField
					label='base.shared.noteVisibleForPatient'
					name='note'
					isWrapped
				/>
				{isLatam && (
					<CheckboxField
						name='paidMeeting'
						tx='component.calendarPatientEvent.paidMeeting'
						isWrapped
					/>
				)}
				<div className='mt-4'>
					<SubmitButton
						title='component.eventDetail.saveToCalendarAndSendInfoToPatient'
						onSubmit={handleSave}
						disabled={isLoading}
					/>
				</div>
			</FormWrapper>
		</>
	);
};

export default AppointmentDetail;
